// Paper Review (Units 18 + 19 + 20 + 21 + 22 + 23 + 24).
//
// Layout (spec §11.2):
//   .scan-area  (flex 1)   ScanCanvas + Konva overlay + AnnotationCreator
//   .transcript (flex 1)   TranscriptView                — only when toggled
//   .details    (40vw)     AnnotationCards + ScoreDisplay + recheck button

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { mutate } from "swr";

import { useTranslation } from "react-i18next";

import { apiJson } from "../api/client";
import {
  useAnnotations,
  usePaper,
  usePaperStatus,
  useProjects,
} from "../api/hooks";
import type { AnnotationType, Paper, Project } from "../api/types";
import { AnnotationCards } from "../components/AnnotationCards";
import {
  AnnotationCreator,
  type ManualType as _ManualType,
} from "../components/AnnotationCreator";
import type { WordDoubleClickInfo } from "../components/AnnotationOverlay";
import { FeedbackEditor } from "../components/FeedbackEditor";
import { HeaderActions } from "../components/HeaderActions";
import { QuotaBanner } from "../components/QuotaBanner";
import { ScanCanvas, MIN_ZOOM, MAX_ZOOM } from "../components/ScanCanvas";
import { RasterPanel } from "../components/RasterPanel";
import { ScoreDisplay } from "../components/ScoreDisplay";
import { Tooltip } from "../components/Tooltip";
import { TranscriptView } from "../components/TranscriptView";
import { useDeviceMode } from "../device/DeviceContext";
import { useKeyboard } from "../hooks/useKeyboard";
import { trackEvent } from "../telemetry/useTelemetry";
import { exportPaperPdf, PaperExportError } from "../export/exportPaper";
import { debounce } from "../lib/debounce";
import { annotationsKey, useAnnotationStore } from "../state/annotationStore";
import { useTourStore } from "../state/tourStore";
import { advanceTour } from "../components/GuidedTour";
import { SettingsModal } from "./Settings";

import "./PaperReview.css";

export function PaperReview() {
  const { t } = useTranslation();
  const params = useParams<{ paperId: string }>();
  const paperId = params.paperId ? Number(params.paperId) : null;
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const mode = useDeviceMode();
  // Mobile-specific layout state.
  const [mobileShowTranscript, setMobileShowTranscript] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  const [detailsHeight, setDetailsHeight] = useState(() => Math.round(window.innerHeight * 0.35));
  const detailsDragRef = useRef<{ startY: number; startH: number } | null>(null);
  const mobileZoomInitRef = useRef(false);

  // Page dimensions for transcript line sync (page_number → heightPx).
  const [pageDimensions, setPageDimensions] = useState<Map<number, number>>(new Map());
  const onPageHeight = useCallback((page: number, h: number) => {
    setPageDimensions((prev) => {
      if (prev.get(page) === h) return prev;
      const next = new Map(prev);
      next.set(page, h);
      return next;
    });
  }, []);

  // Footnote area heights (page_number → footnoteHeightPx) for transcript offset.
  const [footnoteDimensions, setFootnoteDimensions] = useState<Map<number, number>>(new Map());
  const onPageFootnoteHeight = useCallback((page: number, h: number) => {
    setFootnoteDimensions((prev) => {
      if (prev.get(page) === h) return prev;
      const next = new Map(prev);
      next.set(page, h);
      return next;
    });
  }, []);

  // Scroll sync between scan canvas and transcript.
  const scanScrollRef = useRef<HTMLDivElement>(null);
  const transcriptScrollRef = useRef<HTMLDivElement>(null);
  const isSyncing = useRef(false);
  const lastScanScrollTopRef = useRef(0);
  const onScanScroll = useCallback(() => {
    // Auto-hide the mobile header while scrolling down; reveal when near top or scrolling up.
    if (mode === "mobile" && scanScrollRef.current) {
      const st = scanScrollRef.current.scrollTop;
      if (st > 10 && st > lastScanScrollTopRef.current) setHeaderHidden(true);
      else if (st <= 10 || st < lastScanScrollTopRef.current - 5) setHeaderHidden(false);
      lastScanScrollTopRef.current = st;
    }
    if (isSyncing.current || !transcriptScrollRef.current || !scanScrollRef.current) return;
    isSyncing.current = true;
    // On desktop the scan has a one-viewport empty pad above the pages (padEnds);
    // the transcript has none, so offset the 1:1 sync by that pad height.
    const pad = mode !== "mobile" ? scanScrollRef.current.clientHeight : 0;
    transcriptScrollRef.current.scrollTop = scanScrollRef.current.scrollTop - pad;
    requestAnimationFrame(() => { isSyncing.current = false; });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);
  const onTranscriptScroll = useCallback(() => {
    if (isSyncing.current || !scanScrollRef.current || !transcriptScrollRef.current) return;
    isSyncing.current = true;
    const pad = mode !== "mobile" ? scanScrollRef.current.clientHeight : 0;
    scanScrollRef.current.scrollTop = transcriptScrollRef.current.scrollTop + pad;
    requestAnimationFrame(() => { isSyncing.current = false; });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const paperSwr = usePaper(paperId);
  const annotationsSwr = useAnnotations(paperId);
  const statusSwr = usePaperStatus(paperId);
  const tourActive = useTourStore((s) => s.active);
  const projectsSwr = useProjects(false, tourActive);

  const project: Project | undefined = useMemo(() => {
    if (!projectsSwr.data || !paperSwr.data) return undefined;
    return projectsSwr.data.projects.find((p) => p.id === paperSwr.data!.project_id);
  }, [projectsSwr.data, paperSwr.data]);

  // Sibling papers from the same project — drives the dropdown + prev/next.
  const siblings = project?.papers ?? [];
  const currentIdx = useMemo(
    () => siblings.findIndex((p) => p.id === paperId),
    [siblings, paperId],
  );
  function gotoSibling(offset: -1 | 1) {
    if (currentIdx < 0) return;
    const next = siblings[currentIdx + offset];
    if (next) navigate(`/review/${next.id}`);
  }
  const [paperMenuOpen, setPaperMenuOpen] = useState(false);

  const hydrate = useAnnotationStore((s) => s.hydrate);
  const rerunLanguage = useAnnotationStore((s) => s.rerunLanguage);
  const select = useAnnotationStore((s) => s.select);
  const accept = useAnnotationStore((s) => s.accept);
  const dismiss = useAnnotationStore((s) => s.dismiss);
  const confirm = useAnnotationStore((s) => s.confirm);
  const selectedId = useAnnotationStore((s) => s.selectedId);
  const frameRegion = useAnnotationStore((s) => s.frameRegion);

  // Mirror SWR annotations into the Zustand store. Resets on paper change.
  useEffect(() => {
    if (annotationsSwr.data) {
      hydrate(paperId, annotationsSwr.data.annotations);
    }
  }, [annotationsSwr.data, paperId, hydrate]);

  // Auto-advance: when the selected annotation gets resolved, select the next pending one.
  const storeAnnotations = useAnnotationStore((s) => s.annotations);
  const prevStoreRef = useRef<typeof storeAnnotations>([]);
  const selectedIdRef = useRef(selectedId);
  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);
  useEffect(() => {
    const prev = prevStoreRef.current;
    const curr = storeAnnotations;
    const selId = selectedIdRef.current;
    if (selId != null && prev.length > 0) {
      const prevAnn = prev.find((a) => a.id === selId);
      const currAnn = curr.find((a) => a.id === selId);
      if (prevAnn?.status === "pending" && currAnn && currAnn.status !== "pending") {
        const prevPending = prev.filter((a) => a.status === "pending").sort(byPosition).map((a) => a.id);
        const idx = prevPending.indexOf(selId);
        const currPending = curr.filter((a) => a.status === "pending").sort(byPosition).map((a) => a.id);
        select(currPending[idx] ?? currPending[Math.max(0, currPending.length - 1)] ?? null);
      }
    }
    prevStoreRef.current = curr;
  }, [storeAnnotations, select]);


  // Telemetry: track open/close as a pair so session timings can be derived.
  useEffect(() => {
    if (paperId == null) return;
    trackEvent("paper.review.open", { paper_id: paperId });
    return () => trackEvent("paper.review.close", { paper_id: paperId });
  }, [paperId]);

  // Reset mobile ephemeral state when navigating to a different paper.
  useEffect(() => {
    setMobileShowTranscript(false);
    setHeaderHidden(false);
    lastScanScrollTopRef.current = 0;
    mobileZoomInitRef.current = false;
  }, [paperId]);

  // Ref for the desktop cards-wrap panel — used to locate the selected card's
  // current vertical position when aligning the scan scroll.
  const cardsWrapRef = useRef<HTMLDivElement>(null);

  // When the selected annotation changes, scroll only the scan so the flagged
  // word lines up with its card at the card's current position — the cards panel
  // never moves. This works for every card because the scan is padded with one
  // viewport of empty runway above and below the pages (ScanCanvas padEnds), so
  // the word can be scrolled to any vertical position without scrollTop clamping.
  useEffect(() => {
    if (selectedId == null) return;
    const ann = storeAnnotations.find((a) => a.id === selectedId);
    if (!ann) return;
    if (!scanScrollRef.current) return;
    // Defer one frame: selecting a resolved row also expands it into a full
    // card, so the card's geometry only settles after the next layout. Measure
    // then, otherwise positions are read off the pre-expansion (or stale) DOM.
    const raf = requestAnimationFrame(() => {
      const el = scanScrollRef.current;
      if (!el) return;
      // Word position is measured live (the overlay draws each word at
      // y_norm * canvasHeight down from the page div's top edge, where the canvas
      // sits flush) rather than reconstructed from page-height maps.
      const pageEl = el.querySelector<HTMLElement>(
        `.scan-canvas__page[data-page="${ann.page_number}"]`,
      );
      if (!pageEl) return;
      const cardEl = cardsWrapRef.current?.querySelector<HTMLElement>(
        `[data-annotation-id="${selectedId}"]`,
      );
      // Card anchor: the "<em>word</em> → suggestion" line, else the content
      // note, a resolved row's summary, then the whole card — so every type aligns.
      const target =
        cardEl?.querySelector<HTMLElement>(".card__original, .card__note, .resolved-summary") ??
        cardEl;
      const canvasH = pageEl.querySelector("canvas")?.offsetHeight ?? pageEl.clientHeight;
      const wordViewportY =
        pageEl.getBoundingClientRect().top + (ann.y_norm + (ann.height_norm ?? 0) / 2) * canvasH;
      if (target) {
        // Shift the scan so the word's current screen position meets the card's.
        const cardRect = target.getBoundingClientRect();
        el.scrollTop += wordViewportY - (cardRect.top + cardRect.height / 2);
      } else {
        // No matching card (e.g. clicking evidence in the Raster panel) — just
        // centre the flagged word in the scan viewport.
        el.scrollTop += wordViewportY - (el.getBoundingClientRect().top + el.clientHeight / 2);
      }
    });
    return () => cancelAnimationFrame(raf);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // Clicking a content task frames its answer region on the scan — scroll the
  // scan so that region is centred in the viewport (docs/05 §5).
  useEffect(() => {
    if (!frameRegion || !scanScrollRef.current) return;
    const raf = requestAnimationFrame(() => {
      const el = scanScrollRef.current;
      if (!el) return;
      const pageEl = el.querySelector<HTMLElement>(
        `.scan-canvas__page[data-page="${frameRegion.page}"]`,
      );
      if (!pageEl) return;
      const canvasH = pageEl.querySelector("canvas")?.offsetHeight ?? pageEl.clientHeight;
      const midNorm = (frameRegion.bbox[1] + frameRegion.bbox[3]) / 2;
      const regionViewportY = pageEl.getBoundingClientRect().top + midNorm * canvasH;
      const centerY = el.getBoundingClientRect().top + el.clientHeight / 2;
      el.scrollTop += regionViewportY - centerY;
    });
    return () => cancelAnimationFrame(raf);
  }, [frameRegion]);

  // Track the current visible page so we can debounce a PATCH on scroll.
  const [activePage, setActivePage] = useState<number>(
    paperSwr.data?.last_active_page ?? 1,
  );
  const persistPage = useMemo(
    () =>
      debounce((page: number) => {
        if (!paperId) return;
        void apiJson(`/api/papers/${paperId}`, {
          method: "PATCH",
          body: JSON.stringify({ last_active_page: page }),
        }).catch(() => {});
      }, 1000),
    [paperId],
  );
  useEffect(() => () => persistPage.cancel(), [persistPage]);

  function onActivePageChange(page: number) {
    setActivePage(page);
    persistPage(page);
  }

  // Model solution pane toggle + resizable width.
  const [modelOpen, setModelOpen] = useState(false);
  const [modelWidth, setModelWidth] = useState(320);
  const [modelDividerActive, setModelDividerActive] = useState(false);
  const modelDragRef = useRef<{ startX: number; startWidth: number; maxWidth: number } | null>(null);
  const modelEverOpenedRef = useRef(false);

  function startModelDrag(e: React.MouseEvent) {
    e.preventDefault();
    const maxWidth = scanRef.current ? scanRef.current.clientWidth - 300 : 800;
    modelDragRef.current = { startX: e.clientX, startWidth: modelWidth, maxWidth };
    setModelDividerActive(true);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    function onMove(ev: MouseEvent) {
      if (!modelDragRef.current) return;
      const delta = ev.clientX - modelDragRef.current.startX;
      setModelWidth(Math.max(180, Math.min(modelDragRef.current.maxWidth, modelDragRef.current.startWidth + delta)));
    }
    function onUp() {
      modelDragRef.current = null;
      setModelDividerActive(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  // Transcript pane toggle + resizable width.
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [transcriptWidth, setTranscriptWidth] = useState(320);
  const [transcriptDividerActive, setTranscriptDividerActive] = useState(false);
  const transcriptDragRef = useRef<{ startX: number; startWidth: number; maxWidth: number } | null>(null);
  const transcriptEverOpenedRef = useRef(false);

  function startTranscriptDrag(e: React.MouseEvent) {
    e.preventDefault();
    const maxWidth = scanRef.current ? scanRef.current.clientWidth - 300 : 800;
    transcriptDragRef.current = { startX: e.clientX, startWidth: transcriptWidth, maxWidth };
    setTranscriptDividerActive(true);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    function onMove(ev: MouseEvent) {
      if (!transcriptDragRef.current) return;
      const delta = ev.clientX - transcriptDragRef.current.startX;
      setTranscriptWidth(Math.max(180, Math.min(transcriptDragRef.current.maxWidth, transcriptDragRef.current.startWidth + delta)));
    }
    function onUp() {
      transcriptDragRef.current = null;
      setTranscriptDividerActive(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }


  // ── Keyboard shortcuts (spec §11.12) ─────────────────────────────
  const annotationList = annotationsSwr.data?.annotations ?? [];
  const visibleAnnotations = useMemo(
    () => annotationList.filter((a) => a.status === "pending").sort(byPosition),
    [annotationList],
  );

  function moveSelection(direction: 1 | -1) {
    if (visibleAnnotations.length === 0) return;
    const ids = visibleAnnotations.map((a) => a.id);
    const current = selectedId == null ? -1 : ids.indexOf(selectedId);
    const next = ids[(current + direction + ids.length) % ids.length];
    if (next != null) select(next);
  }

  useKeyboard(
    useMemo(
      () => [
        { key: "j", handler: () => moveSelection(1) },
        { key: "k", handler: () => moveSelection(-1) },
        { key: "ArrowDown", handler: () => moveSelection(1) },
        { key: "ArrowUp", handler: () => moveSelection(-1) },
        {
          key: "a",
          handler: () => {
            if (selectedId != null) {
              trackEvent("annotation.accept", { annotation_id: selectedId });
              void accept(selectedId);
            }
          },
        },
        {
          key: "d",
          handler: () => {
            if (selectedId != null) {
              trackEvent("annotation.dismiss", { annotation_id: selectedId });
              void dismiss(selectedId);
            }
          },
        },
        {
          key: "c",
          handler: () => {
            if (selectedId != null) {
              trackEvent("ocr_flag.confirm", { annotation_id: selectedId });
              void confirm(selectedId);
            }
          },
        },
        {
          key: "m",
          handler: () => {
            setTranscriptOpen((v) => !v);
            if (!transcriptEverOpenedRef.current && scanRef.current) {
              setTranscriptWidth(Math.round(scanRef.current.clientWidth * 0.4));
              transcriptEverOpenedRef.current = true;
            }
            trackEvent("transcript.toggle");
          },
        },
        { key: "r", handler: () => paperSwr.data && (window.history.length > 1 ? window.history.back() : null) },
      ],
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [selectedId, visibleAnnotations.length],
    ),
  );

  // Update button: turns green when OCR annotations change after the last run.
  const ocrBaselineRef = useRef<Map<number, string> | null>(null);
  const [ocrDirty, setOcrDirty] = useState(false);
  // null = idle; any string = spinner label shown in the toolbar
  const [rerunStatus, setRerunStatus] = useState<string | null>(null);
  // Raster view's content-only re-run (re-runs the content_analysis stage).
  const [contentRerunStatus, setContentRerunStatus] = useState<string | null>(null);

  useEffect(() => {
    if (ocrBaselineRef.current === null) return;
    const ocrAnns = annotationList.filter((a) => a.type === "ocr_flag");
    const baseline = ocrBaselineRef.current;
    const dirty =
      ocrAnns.some((a) => baseline.get(a.id) !== a.status) ||
      ocrAnns.length !== baseline.size;
    setOcrDirty(dirty);
  }, [annotationList]);

  async function handleUpdate() {
    if (paperId == null || rerunStatus != null) return;
    const ocrAnns = annotationList.filter((a) => a.type === "ocr_flag");
    ocrBaselineRef.current = new Map(ocrAnns.map((a) => [a.id, a.status]));
    setOcrDirty(false);

    // Snapshot current language annotation IDs so we can detect when
    // LanguageTool has finished inserting new rows.
    const prevLangIds = new Set(
      annotationList.filter((a) => a.type === "language").map((a) => a.id),
    );

    try {
      setRerunStatus(t("paper_review.rerun_sending"));
      // POST + first mutate (annotations become "invalidated" immediately).
      await rerunLanguage(paperId);

      setRerunStatus(t("paper_review.rerun_waiting"));
      // Poll every 500 ms until new pending language annotations appear.
      // Safety exit: if nothing new appears for 8 s, LanguageTool found no
      // errors (or a new paper where the baseline was empty).
      let stableSince = 0;
      for (let i = 0; i < 60; i++) {
        await new Promise<void>((resolve) => window.setTimeout(resolve, 500));
        const fresh = await annotationsSwr.mutate();
        const anns = fresh?.annotations ?? [];
        const hasNew = anns.some(
          (a) => a.type === "language" && a.status === "pending" && !prevLangIds.has(a.id),
        );
        if (hasNew) break;
        if (++stableSince >= 16) break; // 8 s stable → no new errors found
      }
    } finally {
      setRerunStatus(null);
    }
  }

  // Raster view: re-run only the content_analysis stage, then refresh the
  // annotation list + ratings so the new Claude evidence and suggested content
  // scores appear. The stage is fire-and-forget (202); poll status until it
  // leaves the processing states.
  async function handleUpdateContent() {
    if (paperId == null || contentRerunStatus != null) return;
    try {
      setContentRerunStatus(t("paper_review.rerun_content"));
      await apiJson(`/api/papers/${paperId}/run_stage`, {
        method: "POST",
        body: JSON.stringify({ stage: "content_analysis" }),
      });
      for (let i = 0; i < 240; i++) {
        await new Promise<void>((resolve) => window.setTimeout(resolve, 1000));
        const snap = await statusSwr.mutate();
        const s = snap?.status;
        if (s && s !== "processing" && s !== "staged" && s !== "queued") break;
      }
      await annotationsSwr.mutate();
      await mutate(`/api/papers/${paperId}/ratings`);
    } finally {
      setContentRerunStatus(null);
    }
  }

  // Annotation type visibility filter — all on by default. Content is graded in
  // the Raster view (per task), so it is no longer a filterable card type here.
  const ALL_TYPES: AnnotationType[] = ["ocr_flag", "language", "note"];
  const [visibleTypes, setVisibleTypes] = useState<Set<AnnotationType>>(new Set(ALL_TYPES));
  function toggleType(type: AnnotationType) {
    setVisibleTypes((prev) => {
      const next = new Set(prev);
      next.has(type) ? next.delete(type) : next.add(type);
      return next;
    });
  }

  // Right-panel view: annotation cards vs. the raster rating grid. Only relevant
  // for raster projects (the toggle is hidden otherwise); the score area below
  // is shown in both views.
  const [detailsView, setDetailsView] = useState<"cards" | "raster">("cards");
  const isRaster = project != null; // grading is always raster
  const showRaster = isRaster && detailsView === "raster";

  // Single context-aware switch — only for raster projects. In Annotations view
  // it reads "Raster" and switches to the raster grid; in Raster view it reads
  // "Anmerkungen" and switches back.
  const viewToggle = isRaster ? (
    <button
      type="button"
      className="review__btn review__view-switch"
      onClick={() => setDetailsView((v) => (v === "raster" ? "cards" : "raster"))}
      aria-label={t("paper_review.view_toggle")}
    >
      {showRaster ? t("paper_review.view_cards") : t("paper_review.view_raster")}
    </button>
  ) : null;

  // Context-aware Update button: Annotations view re-runs language analysis;
  // Raster view re-runs content analysis. Rendered with its inline spinner.
  const updateControls = showRaster ? (
    <>
      <button
        type="button"
        className="review__btn"
        onClick={() => void handleUpdateContent()}
        disabled={contentRerunStatus != null}
        title={t("paper_review.update_content_title")}
      >
        {t("paper_review.update_btn")}
      </button>
      {contentRerunStatus != null && (
        <span className="review__rerun-status">
          <span className="review__spinner" aria-hidden="true" />
          {contentRerunStatus}
        </span>
      )}
    </>
  ) : (
    <>
      <button
        type="button"
        className={"review__btn" + (ocrDirty ? " review__btn--update" : "")}
        onClick={() => void handleUpdate()}
        disabled={rerunStatus != null}
        title={t("paper_review.update_language_title")}
      >
        {t("paper_review.update_btn")}
      </button>
      {rerunStatus != null && (
        <span className="review__rerun-status">
          <span className="review__spinner" aria-hidden="true" />
          {rerunStatus}
        </span>
      )}
    </>
  );

  // Export state.
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportSuccess, setExportSuccess] = useState<string | null>(null);

  async function performExport() {
    if (paperId == null) return;
    setExporting(true);
    setExportError(null);
    setExportSuccess(null);
    try {
      await exportPaperPdf(paperId, paperSwr.data?.page_count ?? null);
      const link = document.createElement("a");
      link.href = `/api/papers/${paperId}/export?ts=${Date.now()}`;
      link.download = `${paperSwr.data?.name ?? `paper-${paperId}`}_corrected.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      const msg = err instanceof PaperExportError ? err.message : (err as Error).message;
      setExportError(msg);
    } finally {
      setExporting(false);
    }
  }

  function onExportClick() {
    if (paperId == null) return;
    void performExport();
  }

  // Manual annotation creator state (Unit 22).
  const scanRef = useRef<HTMLDivElement>(null);

  // Zoom — controlled here so header buttons can drive it.
  const [zoom, setZoom] = useState(1.0);
  const zoomRef = useRef(zoom);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  const [naturalPage, setNaturalPage] = useState<{ w: number; h: number } | null>(null);
  const [containerSize, setContainerSize] = useState<{ w: number; h: number } | null>(null);

  // Pinch-to-zoom scroll anchor: content position (in PDF units = px / zoom)
  // of the pinch midpoint, measured from the inner wrapper's top-left so the
  // formula is correct whether or not the content is centered in the viewport.
  // Applied in a useEffect([pageDimensions]) so it fires only after react-pdf
  // has finished resizing the canvas — scrollWidth is then correct.
  const pinchAnchorRef = useRef<{ xNorm: number; yNorm: number; vpX: number; vpY: number } | null>(null);
  useEffect(() => {
    const anchor = pinchAnchorRef.current;
    if (!anchor) return;
    pinchAnchorRef.current = null;
    const el = scanScrollRef.current;
    if (!el) return;
    el.scrollLeft = anchor.xNorm * zoom - anchor.vpX;
    el.scrollTop  = anchor.yNorm * zoom - anchor.vpY;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageDimensions]); // zoom is current because pageDimensions updates after zoom

  // Reset zoom + natural size when the paper changes.
  useEffect(() => {
    setZoom(1.0);
    setNaturalPage(null);
  }, [paperId]);

  // Auto-fit scan to full viewport width on mobile (fires once per paper once
  // the first page has rendered and containerSize is known).
  const mobileZoom = naturalPage && containerSize && mode === "mobile"
    ? Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, containerSize.w / naturalPage.w))
    : null;
  useEffect(() => {
    if (mobileZoom == null || mobileZoomInitRef.current) return;
    mobileZoomInitRef.current = true;
    setZoom(mobileZoom);
  }, [mobileZoom]);

  // Pinch-to-zoom on mobile: passive touch handlers on the scan-canvas element.
  // Runs after every render (no dep array) so the listener is always attached to
  // the current scanScrollRef.current once ScanCanvas has mounted.
  const pinchDistRef = useRef<number | null>(null);
  useEffect(() => {
    if (mode !== "mobile") return;
    const el = scanScrollRef.current;
    if (!el) return;
    function onTouchStart(e: TouchEvent) {
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        pinchDistRef.current = Math.sqrt(dx * dx + dy * dy);
      } else {
        pinchDistRef.current = null;
      }
    }
    function onTouchMove(e: TouchEvent) {
      if (e.touches.length !== 2 || pinchDistRef.current === null) return;
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const scale = dist / pinchDistRef.current;
      pinchDistRef.current = dist;
      // Capture the content point under the pinch midpoint in zoom-normalised
      // units (px / zoom) so useLayoutEffect can restore it after the DOM resizes.
      const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      const scrollEl = scanScrollRef.current;
      if (scrollEl) {
        const elRect = scrollEl.getBoundingClientRect();
        // Measure from the inner wrapper's actual left/top so the formula
        // is correct when the content is narrower than the viewport and
        // centred via margin:auto (where scrollLeft is 0 but the wrapper
        // is not at elRect.left).
        const inner = scrollEl.querySelector<HTMLElement>(".scan-canvas__scroll-inner");
        const innerRect = inner?.getBoundingClientRect() ?? elRect;
        const currentZoom = zoomRef.current;
        pinchAnchorRef.current = {
          xNorm: (midX - innerRect.left) / currentZoom,
          yNorm: (midY - innerRect.top)  / currentZoom,
          vpX: midX - elRect.left,
          vpY: midY - elRect.top,
        };
      }
      setZoom((z) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z * scale)));
    }
    function onTouchEnd() { pinchDistRef.current = null; }
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  });

  // Callback ref: starts the ResizeObserver the moment the scan-wrap div mounts
  // (which only happens after paperSwr.data is available, past the early returns).
  const scanWrapRef = useCallback((el: HTMLDivElement | null) => {
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setContainerSize({ w: entry.contentRect.width, h: entry.contentRect.height });
    });
    ro.observe(el);
    // Cleanup is not needed — the element unmounts only when the whole view unmounts.
  }, []);

  const [creator, setCreator] = useState<null | {
    pageNumber: number;
    x: number;
    y: number;
    widthNorm?: number;
    heightNorm?: number;
    screenX: number;
    screenY: number;
  }>(null);

  const onWordDoubleClick = useCallback((info: WordDoubleClickInfo) => {
    setCreator({
      pageNumber: info.pageNumber,
      x: info.xNorm,
      y: info.yNorm,
      widthNorm: info.widthNorm,
      heightNorm: info.heightNorm,
      screenX: info.screenX,
      screenY: info.screenY,
    });
  }, []);

  function onScanClick(e: React.MouseEvent<HTMLDivElement>) {
    // Only fire on clicks directly on the scan-area background — clicks on
    // the canvas / Konva stage / annotation cards already bubble up but
    // aren't candidates for "place a new annotation here".
    const target = e.target as HTMLElement;
    if (target.closest(".konvajs-content")) return;
    if (target.closest(".creator")) return;
    const pageEl = target.closest(".scan-canvas__page") as HTMLElement | null;
    if (!pageEl) return;
    const pageNumber = Number(pageEl.getAttribute("data-page") ?? "0");
    if (!pageNumber) return;
    const canvas = pageEl.querySelector("canvas") as HTMLCanvasElement | null;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    if (x < 0 || x > 1 || y < 0 || y > 1) return;
    setCreator({ pageNumber, x, y, screenX: e.clientX, screenY: e.clientY });
  }

  if (paperId == null) return null;
  if (paperSwr.error || annotationsSwr.error) {
    return (
      <section className="review review--error">
        <h2>Couldn't load paper</h2>
        <p>{(paperSwr.error ?? annotationsSwr.error)?.message}</p>
      </section>
    );
  }
  if (!paperSwr.data) return <div className="review__loading">Loading…</div>;

  const isProcessing =
    statusSwr.data?.status === "processing" || statusSwr.data?.status === "staged" || statusSwr.data?.status === "queued";

  const reviewStatus = paperSwr.data.status === "completed"
    ? t("paper_review.status_done")
    : paperSwr.data.status === "in_review"
      ? t("paper_review.status_in_review")
      : paperSwr.data.status === "ready"
        ? t("paper_review.status_ready")
        : null;

  // Fit-zoom levels derived from the first rendered page + current wrap size.
  // Subtract scan-canvas padding (20px × 2 horizontal, 32px × 2 vertical) so the
  // page doesn't overflow the padded content area and clip on the left edge.
  const fitWidthZoom = naturalPage && containerSize
    ? Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, (containerSize.w - 40) / naturalPage.w))
    : null;
  const fitPageZoom = naturalPage && containerSize
    ? Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.min((containerSize.w - 40) / naturalPage.w, (containerSize.h - 64) / naturalPage.h)))
    : null;
  const zoomStep = fitWidthZoom != null && fitPageZoom != null && fitWidthZoom > fitPageZoom
    ? (fitWidthZoom - fitPageZoom) / 10
    : 0.1;

  // ── Mobile layout ─────────────────────────────────────────────────────
  if (mode === "mobile") {
    const MIN_DETAILS_H = 120;
    const MAX_DETAILS_H = Math.round(window.innerHeight * 0.72);

    function onHandleMouseDown(e: React.MouseEvent) {
      e.preventDefault();
      detailsDragRef.current = { startY: e.clientY, startH: detailsHeight };
      function onMove(ev: MouseEvent) {
        if (!detailsDragRef.current) return;
        const dy = detailsDragRef.current.startY - ev.clientY;
        setDetailsHeight(Math.max(MIN_DETAILS_H, Math.min(MAX_DETAILS_H, detailsDragRef.current.startH + dy)));
      }
      function onUp() {
        detailsDragRef.current = null;
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      }
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    }
    function onHandleTouchStart(e: React.TouchEvent) {
      detailsDragRef.current = { startY: e.touches[0].clientY, startH: detailsHeight };
    }
    function onHandleTouchMove(e: React.TouchEvent) {
      if (!detailsDragRef.current) return;
      const dy = detailsDragRef.current.startY - e.touches[0].clientY;
      setDetailsHeight(Math.max(MIN_DETAILS_H, Math.min(MAX_DETAILS_H, detailsDragRef.current.startH + dy)));
    }
    function onHandleTouchEnd() { detailsDragRef.current = null; }

    return (
      <div className="review">
        <div className={`review__mobile-header-wrap${headerHidden ? " review__mobile-header-wrap--hidden" : ""}`}>
        <header className="app-header review__header">
          <Link
            to={project ? `/?project=${project.id}&paper=${paperId}` : "/"}
            className="app-header__logo"
            aria-label={t("header.logo_alt")}
          >
            <img src="/logo.svg" alt="" width={28} height={28} />
            <span className="app-header__wordmark">ophelia</span>
          </Link>

          <button
            type="button"
            className={`app-header__btn app-header__btn--ghost review__mobile-transcript-btn${mobileShowTranscript ? " app-header__btn--active" : ""}`}
            onClick={() => setMobileShowTranscript((v) => !v)}
            aria-label={t("paper_review.transcript")}
          >
            <IconPage />
            <span className="app-header__btn-label">{t("paper_review.transcript")}</span>
          </button>

          <div className="review__header-filter-group">
            {(
              [
                { type: "ocr_flag" as AnnotationType, labelKey: "paper_review.filter_ocr" },
                { type: "language" as AnnotationType, labelKey: "paper_review.filter_language" },
                { type: "note" as AnnotationType, labelKey: "paper_review.filter_notes" },
              ]
            ).map(({ type, labelKey }) => (
              <button
                key={type}
                type="button"
                className={"review__filter-btn" + (visibleTypes.has(type) ? " review__filter-btn--on" : "")}
                onClick={() => toggleType(type)}
                title={t(labelKey)}
              >
                {t(labelKey)}
              </button>
            ))}
          </div>
        </header>
        </div>

        {settingsOpen && <SettingsModal onClose={() => setSettingsOpen(false)} />}

        <div className="review__body review__body--mobile-v2">
          <div className="review__main" ref={scanRef} onClick={onScanClick}>
            {mobileShowTranscript ? (
              <div className="review__mobile-transcript" data-tour="transcript-panel">
                <TranscriptView
                  paperId={paperId}
                  pageDimensions={pageDimensions}
                  footnoteDimensions={footnoteDimensions}
                  scrollRef={transcriptScrollRef}
                  onScroll={onTranscriptScroll}
                  visibleTypes={visibleTypes}
                  onWordDoubleClick={onWordDoubleClick}
                />
              </div>
            ) : (
              <div className="review__scan-wrap" ref={scanWrapRef} data-tour="scan-canvas">
                <ScanCanvas
                  paperId={paperId}
                  pageCount={paperSwr.data.page_count}
                  initialPage={paperSwr.data.last_active_page ?? 1}
                  onActivePageChange={onActivePageChange}
                  zoom={zoom}
                  onZoomChange={setZoom}
                  onFirstPageRendered={(w, h) => setNaturalPage({ w, h })}
                  scrollRef={scanScrollRef}
                  onPageHeight={onPageHeight}
                  onPageFootnoteHeight={onPageFootnoteHeight}
                  onWordDoubleClick={onWordDoubleClick}
                  onScroll={onScanScroll}
                  visibleTypes={visibleTypes}
                  padEnds={mode !== "mobile"}
                />
              </div>
            )}
          </div>

          <aside
            className="review__details review__details--mobile"
            style={{ height: detailsHeight }}
          >
            <div
              className="review__details-handle"
              onMouseDown={onHandleMouseDown}
              onTouchStart={onHandleTouchStart}
              onTouchMove={onHandleTouchMove}
              onTouchEnd={onHandleTouchEnd}
            />
            <div className="review__details-scroll">
              {isProcessing && (
                <div className="review__processing">
                  {statusSwr.data?.stage_label ?? t("paper_review.processing_fallback")}
                  {statusSwr.data?.page && statusSwr.data?.of && (
                    <> — page {statusSwr.data.page} of {statusSwr.data.of}</>
                  )}
                </div>
              )}
              {exportError && (
                <div className="review__export-error" role="alert">{exportError}</div>
              )}
              {paperSwr.data.status === "quota_exceeded" && <QuotaBanner paperId={paperId} />}
              {isRaster && (
                <div className="review__toolbar review__toolbar--mobile">
                  {updateControls}
                  {viewToggle}
                </div>
              )}
              {showRaster && project ? (
                <RasterPanel project={project} paper={paperSwr.data} />
              ) : (
                <div className="review__cards-wrap" data-tour="annotation-cards">
                  <AnnotationCards visibleTypes={visibleTypes} />
                  <FeedbackEditor
                    paper={paperSwr.data}
                    onPatched={(updated) => { void paperSwr.mutate(updated, { revalidate: false }); }}
                  />
                </div>
              )}
              {project && (
                <ScoreDisplay
                  project={project}
                  paper={paperSwr.data}
                  onPatched={(updated: Paper) => { void paperSwr.mutate(updated, { revalidate: false }); }}
                  onExport={onExportClick}
                  exporting={exporting}
                  isProcessing={isProcessing}
                />
              )}
            </div>
          </aside>
        </div>

        {creator && (
          <AnnotationCreator
            paperId={paperId}
            pageNumber={creator.pageNumber}
            x={creator.x}
            y={creator.y}
            widthNorm={creator.widthNorm}
            heightNorm={creator.heightNorm}
            screenX={creator.screenX}
            screenY={creator.screenY}
            onClose={() => { setCreator(null); void mutate(annotationsKey(paperId)); }}
          />
        )}
      </div>
    );
  }

  // ── Desktop layout ─────────────────────────────────────────────────────
  return (
    <div className="review">
      <header className="app-header review__header">
        <div className="app-header__logo" data-tour="return-btn">
          <Link to="/" aria-label={t("header.logo_alt")}>
            <img src="/logo.svg" alt="" width={28} height={28} />
          </Link>
          <Link to={project ? `/?project=${project.id}&paper=${paperId}` : "/"} className="app-header__wordmark">ophelia</Link>
        </div>

        <div className="review__header-divider" aria-hidden />

        <div className="review__header-stack">
          {project && (
            <Link
              to={`/?project=${project.id}`}
              className="review__header-project"
            >
              {project.name}
            </Link>
          )}
          <div className="review__header-paper-row">
            <div className="review__paper-dropdown">
              <button
                type="button"
                className="review__paper-dropdown-btn"
                onClick={() => setPaperMenuOpen((v) => !v)}
                aria-expanded={paperMenuOpen}
              >
                {paperSwr.data.name}
                <span className="review__paper-dropdown-chev" aria-hidden>▼</span>
              </button>
              {paperMenuOpen && siblings.length > 0 && (
                <div className="review__paper-menu" role="menu">
                  {siblings.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className={
                        "review__paper-menu-item" +
                        (p.id === paperId ? " review__paper-menu-item--active" : "")
                      }
                      onClick={() => {
                        setPaperMenuOpen(false);
                        navigate(`/review/${p.id}`);
                      }}
                    >
                      {p.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              className="review__paper-nav-btn"
              onClick={() => gotoSibling(-1)}
              disabled={currentIdx <= 0}
              aria-label={t("paper_review.prev_paper")}
            >
              ‹
            </button>
            <button
              type="button"
              className="review__paper-nav-btn"
              onClick={() => gotoSibling(1)}
              disabled={currentIdx < 0 || currentIdx >= siblings.length - 1}
              aria-label={t("paper_review.next_paper")}
            >
              ›
            </button>
            {reviewStatus && (
              <span className="review__status">{reviewStatus}</span>
            )}
          </div>
        </div>

        <div className="review__header-center">
          <div className="review__header-page-info" aria-hidden>
            {t("paper_review.page_indicator", {
              page: activePage,
              total: paperSwr.data.page_count ?? "—",
            })}
            {paperSwr.data.word_count != null && (
              <>
                <span className="review__header-sep">·</span>
                {t("paper_review.word_count", { count: paperSwr.data.word_count })}
              </>
            )}
          </div>
          <div className="review__zoom-controls" aria-label="Zoom">
            <button
              type="button"
              className="review__zoom-btn"
              onClick={() => {
                if (fitWidthZoom == null || fitPageZoom == null) return;
                const atFitWidth = Math.abs(zoom - fitWidthZoom) < 0.001;
                setZoom(atFitWidth ? fitPageZoom : fitWidthZoom);
              }}
              disabled={fitWidthZoom == null || fitPageZoom == null}
              title={fitWidthZoom != null && Math.abs(zoom - fitWidthZoom) < 0.001 ? "Fit page" : "Fit width"}
            >↔</button>
            <button
              type="button"
              className="review__zoom-btn"
              onClick={() => setZoom((z) => Math.max(fitPageZoom ?? MIN_ZOOM, z - zoomStep))}
              disabled={fitPageZoom == null || zoom <= fitPageZoom + 0.001}
              title="Zoom out"
            >−</button>
            <button
              type="button"
              className="review__zoom-btn"
              onClick={() => setZoom((z) => Math.min(MAX_ZOOM, z + zoomStep))}
              disabled={zoom >= MAX_ZOOM - 0.001}
              title="Zoom in"
            >+</button>
          </div>
        </div>

        <div className="app-header__spacer" />

        <div className="review__panel-btns">
          {project && (
            <button
              type="button"
              className={
                "app-header__btn app-header__btn--ghost" +
                (modelOpen ? " app-header__btn--active" : "")
              }
              onClick={() => {
                const opening = !modelOpen;
                setModelOpen(opening);
                if (opening) {
                  setTranscriptOpen(false);
                  if (!modelEverOpenedRef.current && scanRef.current) {
                    setModelWidth(Math.round(scanRef.current.clientWidth * 0.4));
                    modelEverOpenedRef.current = true;
                  }
                }
                trackEvent("paper.model_solution.open", { paper_id: paperId });
              }}
              title={t("paper_review.model_solution")}
            >
              <IconSquare /><span className="app-header__btn-label">{t("paper_review.model_solution")}</span>
            </button>
          )}

          <Tooltip tipKey="transcript_toggle">
            <button
              type="button"
              data-tour="transcript-btn"
              className={
                "app-header__btn app-header__btn--ghost" +
                (transcriptOpen ? " app-header__btn--active" : "")
              }
              onClick={() => {
                const opening = !transcriptOpen;
                setTranscriptOpen(opening);
                if (opening) {
                  setModelOpen(false);
                  if (!transcriptEverOpenedRef.current && scanRef.current) {
                    setTranscriptWidth(Math.round(scanRef.current.clientWidth * 0.4));
                    transcriptEverOpenedRef.current = true;
                  }
                  if (tourActive) window.setTimeout(() => advanceTour(), 120);
                }
                trackEvent("transcript.toggle");
              }}
            >
              <IconPage /><span className="app-header__btn-label">{t("paper_review.transcript")}</span>
            </button>
          </Tooltip>
        </div>

        <HeaderActions onSettingsOpen={() => setSettingsOpen(true)} />
      </header>
      {settingsOpen && <SettingsModal onClose={() => setSettingsOpen(false)} />}

      <div className="review__body">
        <div
          className={`review__main${transcriptOpen ? " review__main--with-transcript" : ""}`}
          ref={scanRef}
          onClick={onScanClick}
        >
          {modelOpen && project && (
            <>
              <div className="review__transcript-panel" style={{ width: modelWidth }}>
                <iframe
                  className="review__model-frame"
                  src={`/api/projects/${project.id}/model_solution_pdf`}
                  title={t("paper_review.model_solution")}
                  style={{ width: "100%", height: "100%", border: "none" }}
                />
              </div>
              <div className={"review__transcript-divider" + (modelDividerActive ? " review__transcript-divider--active" : "")} onMouseDown={startModelDrag} />
            </>
          )}
          {transcriptOpen && (
            <>
              <div className="review__transcript-panel" style={{ width: transcriptWidth }} data-tour="transcript-panel">
                <TranscriptView
                  paperId={paperId}
                  pageDimensions={pageDimensions}
                  footnoteDimensions={footnoteDimensions}
                  scrollRef={transcriptScrollRef}
                  onScroll={onTranscriptScroll}
                  visibleTypes={visibleTypes}
                  onWordDoubleClick={onWordDoubleClick}
                />
              </div>
              <div className={"review__transcript-divider" + (transcriptDividerActive ? " review__transcript-divider--active" : "")} onMouseDown={startTranscriptDrag} />
            </>
          )}
          <div className="review__scan-wrap" ref={scanWrapRef} data-tour="scan-canvas">
            <ScanCanvas
              paperId={paperId}
              pageCount={paperSwr.data.page_count}
              initialPage={paperSwr.data.last_active_page ?? 1}
              onActivePageChange={onActivePageChange}
              zoom={zoom}
              onZoomChange={setZoom}
              onFirstPageRendered={(w, h) => setNaturalPage({ w, h })}
              scrollRef={scanScrollRef}
              onPageHeight={onPageHeight}
              onPageFootnoteHeight={onPageFootnoteHeight}
              onWordDoubleClick={onWordDoubleClick}
              onScroll={onScanScroll}
              visibleTypes={visibleTypes}
              padEnds
            />
          </div>
        </div>

        <aside className="review__details">
          {isProcessing && (
            <div className="review__processing">
              {statusSwr.data?.stage_label ?? t("paper_review.processing_fallback")}
              {statusSwr.data?.page && statusSwr.data?.of && (
                <> — page {statusSwr.data.page} of {statusSwr.data.of}</>
              )}
            </div>
          )}
          <div className="review__toolbar">
            {updateControls}
            {viewToggle}
            {!showRaster && (
              <div className="review__filter-group" data-tour="filter-toolbar">
                {(
                  [
                    { type: "ocr_flag", labelKey: "paper_review.filter_ocr" },
                    { type: "language", labelKey: "paper_review.filter_language" },
                    { type: "note", labelKey: "paper_review.filter_notes" },
                  ] as { type: AnnotationType; labelKey: string }[]
                ).map(({ type, labelKey }) => (
                  <button
                    key={type}
                    type="button"
                    className={
                      "review__filter-btn" +
                      (visibleTypes.has(type) ? " review__filter-btn--on" : "")
                    }
                    onClick={() => toggleType(type)}
                    title={t(labelKey)}
                  >
                    {t(labelKey)}
                  </button>
                ))}
              </div>
            )}
          </div>
          {exportError && (
            <div className="review__export-error" role="alert">
              {exportError}
            </div>
          )}
          {exportSuccess && (
            <div className="review__export-success" role="status">
              {exportSuccess}
            </div>
          )}

          {paperSwr.data.status === "quota_exceeded" && (
            <QuotaBanner paperId={paperId} />
          )}

          {showRaster && project ? (
            <RasterPanel project={project} paper={paperSwr.data} />
          ) : (
            <div className="review__cards-wrap" data-tour="annotation-cards" ref={cardsWrapRef}>
              <AnnotationCards visibleTypes={visibleTypes} />
              <FeedbackEditor
                paper={paperSwr.data}
                onPatched={(updated) => {
                  void paperSwr.mutate(updated, { revalidate: false });
                }}
              />
            </div>
          )}

          {project && (
            <ScoreDisplay
              project={project}
              paper={paperSwr.data}
              onPatched={(updated: Paper) => {
                void paperSwr.mutate(updated, { revalidate: false });
              }}
              onExport={onExportClick}
              exporting={exporting}
              isProcessing={isProcessing}
            />
          )}
        </aside>
      </div>

      {creator && (
        <AnnotationCreator
          paperId={paperId}
          pageNumber={creator.pageNumber}
          x={creator.x}
          y={creator.y}
          widthNorm={creator.widthNorm}
          heightNorm={creator.heightNorm}
          screenX={creator.screenX}
          screenY={creator.screenY}
          onClose={() => {
            setCreator(null);
            void mutate(annotationsKey(paperId));
          }}
        />
      )}

    </div>
  );
}

function byPosition(a: { page_number: number; y_norm: number; x_norm: number }, b: { page_number: number; y_norm: number; x_norm: number }): number {
  if (a.page_number !== b.page_number) return a.page_number - b.page_number;
  if (Math.abs(a.y_norm - b.y_norm) > 0.01) return a.y_norm - b.y_norm;
  return a.x_norm - b.x_norm;
}

function IconPage() {
  return (
    <svg width="11" height="13" viewBox="0 0 11 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0 }}>
      <path d="M2 1.5h5L9.5 5v6.5a.5.5 0 0 1-.5.5H2a.5.5 0 0 1-.5-.5V2a.5.5 0 0 1 .5-.5z" />
      <path d="M7 1.5V5h2.5" />
    </svg>
  );
}

function IconSquare() {
  return (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0 }}>
      <rect x="1" y="1" width="9" height="9" rx="1.5" />
    </svg>
  );
}
